import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Card,
    Typography,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Tag,
    Tooltip,
    Popconfirm,
    Upload,
    Select
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    UploadOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import auctionService from '../../services/auctionService';

const { Title } = Typography;
const { TextArea } = Input;

const LotManagement = () => {
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingLot, setEditingLot] = useState(null);
    const [form] = Form.useForm();
    const { auctionId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        loadLots();
    }, [auctionId]);

    const loadLots = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getLots(auctionId);
            if (response.status === 'success') {
                setLots(response.data);
            } else {
                message.error('Failed to load lots');
            }
        } catch (error) {
            console.error('Error loading lots:', error);
            message.error(error.response?.data?.message || 'Failed to load lots');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEdit = async (values) => {
        try {
            setLoading(true);
            if (editingLot) {
                await auctionService.updateLot(auctionId, editingLot.id, values);
                message.success('Lot updated successfully');
            } else {
                await auctionService.createLot(auctionId, values);
                message.success('Lot created successfully');
            }
            setModalVisible(false);
            form.resetFields();
            setEditingLot(null);
            loadLots();
        } catch (error) {
            console.error('Error saving lot:', error);
            message.error(error.response?.data?.message || 'Failed to save lot');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (lotId) => {
        try {
            await auctionService.deleteLot(auctionId, lotId);
            message.success('Lot deleted successfully');
            loadLots();
        } catch (error) {
            console.error('Error deleting lot:', error);
            message.error(error.response?.data?.message || 'Failed to delete lot');
        }
    };

    const handleActivate = async (lotId) => {
        try {
            await auctionService.activateLot(auctionId, lotId);
            message.success('Lot activated successfully');
            loadLots();
        } catch (error) {
            console.error('Error activating lot:', error);
            message.error(error.response?.data?.message || 'Failed to activate lot');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'default',
            active: 'green',
            sold: 'purple',
            unsold: 'red'
        };
        return colors[status] || 'default';
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <a onClick={() => navigate(`/auctions/${auctionId}/lots/${record.slug}`)}>{text}</a>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Starting Price',
            dataIndex: 'starting_price',
            key: 'starting_price',
            render: (price) => `$${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'Reserve Price',
            dataIndex: 'reserve_price',
            key: 'reserve_price',
            render: (price) => `$${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Current Price',
            dataIndex: 'current_price',
            key: 'current_price',
            render: (price) => price ? `$${parseFloat(price).toFixed(2)}` : 'No bids'
        },
        {
            title: 'Bids',
            dataIndex: 'bids_count',
            key: 'bids_count',
            render: (count) => count || 0
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Lot">
                        <Button
                            target="_blank"
                            rel="noopener noreferrer"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/auctions/${auctionId}/lots/${record.slug}`)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Lot">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditingLot(record);
                                form.setFieldsValue(record);
                                setModalVisible(true);
                            }}
                            disabled={record.status === 'active' || record.status === 'sold'}
                        />
                    </Tooltip>
                    <Tooltip title="Activate Lot">
                        <Button
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleActivate(record.id)}
                            disabled={record.status !== 'pending'}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Lot">
                        <Popconfirm
                            title="Are you sure you want to delete this lot?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.status === 'active' || record.status === 'sold'}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div className="lot-management">
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Title level={2}>Manage Lots</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingLot(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                    >
                        Add New Lot
                    </Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={lots}
                    rowKey="id"
                    loading={loading}
                />
            </Card>

            <Modal
                title={editingLot ? 'Edit Lot' : 'Create New Lot'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingLot(null);
                    form.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateEdit}
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: 'Please enter lot title' }]}
                    >
                        <Input placeholder="Enter lot title" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please enter lot description' }]}
                    >
                        <TextArea rows={4} placeholder="Enter lot description" />
                    </Form.Item>

                    <Form.Item
                        name="starting_price"
                        label="Starting Price"
                        rules={[{ required: true, message: 'Please enter starting price' }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                            prefix="$"
                        />
                    </Form.Item>

                    <Form.Item
                        name="reserve_price"
                        label="Reserve Price"
                        rules={[{ required: true, message: 'Please enter reserve price' }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                            prefix="$"
                        />
                    </Form.Item>

                    <Form.Item
                        name="images"
                        label="Images"
                    >
                        <Upload
                            listType="picture"
                            multiple
                            beforeUpload={() => false}
                        >
                            <Button icon={<UploadOutlined />}>Upload Images</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingLot ? 'Update Lot' : 'Create Lot'}
                            </Button>
                            <Button onClick={() => {
                                setModalVisible(false);
                                setEditingLot(null);
                                form.resetFields();
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LotManagement; 